import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useMembers } from '../hooks/useMembers.ts';
import { useSmsQuota } from '../hooks/useSmsQuota';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import { usePaymentRouting } from '../hooks/usePaymentRouting.ts';
import { useCanCalculateBalance } from '../hooks/useCanCalculateBalance.ts';
import api from '../utils/axios.ts';
import { Plus, FileText, AlertCircle, History, CheckCircle, XCircle, Clock, ChevronDown, ChevronUp, Send } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { PermissionPage, PermissionGate } from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';

export default function BulkSmsPage() {
  const { can } = usePermissions();
  const isAdmin = useIsAdmin();
  const [message, setMessage] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [sendToAll, setSendToAll] = useState(false);
  const [search, setSearch] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendStatements, setSendStatements] = useState(false);
  const [statementPeriod, setStatementPeriod] = useState('3'); // 3, 6, 12, 24 months
  const [invalidPlaceholders, setInvalidPlaceholders] = useState([]);
  const [hasValidated, setHasValidated] = useState(false);
  const textareaRef = useRef(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [expandedJob, setExpandedJob] = useState(null);
  const [jobDetails, setJobDetails] = useState({});
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPagination, setHistoryPagination] = useState(null);

  // Available placeholders
  // Get payment routing settings to check if enabled
  const { data: paymentRoutingSettings } = usePaymentRouting();
  const { canCalculateBalance } = useCanCalculateBalance();
  const hasSubgroupTargets = !!(
    paymentRoutingSettings?.subgroup_targets &&
    Object.keys(paymentRoutingSettings.subgroup_targets).length > 0
  );
  const hasPaymentRouting = (paymentRoutingSettings?.contribution_amount > 0) || hasSubgroupTargets;
  
  // House groups have extra statement figures (rent status, overpayment, drive balance).
  const houseStatementPlaceholders = ['house_status', 'arrears', 'overpaid', 'total_paid', 'drive_balance'];

  const availablePlaceholders = sendStatements 
    ? (hasPaymentRouting && canCalculateBalance)
      ? ['member_name', 'group_name', 'house_name', 'statement_link', 'contribution_balance', ...(hasSubgroupTargets ? houseStatementPlaceholders : [])]
      : ['member_name', 'group_name', 'house_name', 'statement_link', ...(hasSubgroupTargets ? houseStatementPlaceholders : [])]
    : ['member_name', 'group_name', 'house_name'];

  // Draft message examples
  const draftExamples = sendStatements 
    ? (hasSubgroupTargets
        ? [
            "Hi {member_name}, your {group_name} statement for {house_name} is ready. Status: {house_status}. Download: {statement_link}",
            "Hello {member_name}, {house_name} at {group_name}: {house_status}. Drive balance: {drive_balance}. Statement: {statement_link}",
            "{member_name}, {house_name} has paid {total_paid} so far. Status: {house_status}. {statement_link}",
            "Hi {member_name}, view your {group_name} statement. {house_name}: {house_status}. Link: {statement_link}"
          ]
        : hasPaymentRouting 
        ? [
            "Hi {member_name}, your {group_name} contribution statement is ready. Remaining to pay: {contribution_balance}. Download: {statement_link}",
            "Hello {member_name}, view your contribution statement for {group_name}. Remaining to pay: {contribution_balance}. Link: {statement_link}",
            "{member_name}, your {group_name} statement is available. Remaining to pay: {contribution_balance}. Get it here: {statement_link}",
            "Hi {member_name}, download your {group_name} contribution statement. Remaining to pay: {contribution_balance}. {statement_link}"
          ]
        : [
            "Hi {member_name}, your {group_name} contribution statement is ready. Download: {statement_link}",
            "Hello {member_name}, view your contribution statement for {group_name}. Link: {statement_link}",
            "{member_name}, your {group_name} statement is available. Get it here: {statement_link}",
            "Hi {member_name}, download your {group_name} contribution statement. {statement_link}"
          ])
    : [
    "Hi {member_name}, this is a reminder from {group_name}.",
    "Hello {member_name}, important update from {group_name}.",
    "{member_name}, please check your {group_name} account.",
    "Hi {member_name}, message from {group_name}."
  ];

  const { data: membersData, isLoading: isMembersLoading } = useMembers();
  const { data: quotaData, isLoading: isQuotaLoading } = useSmsQuota();

  const members = membersData?.data || [];
  const quota = quotaData?.data;

  // Filter members by search
  const filteredMembers = useMemo(() => {
    if (!search) return members;
    return members.filter(m =>
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.phone_number.includes(search) ||
      m.member_no?.includes(search)
    );
  }, [members, search]);

  // Select all filtered members
  const handleSelectAll = () => {
    if (selectedMembers.length === filteredMembers.length) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(filteredMembers.map(m => m.id));
    }
  };

  // Toggle individual member
  const handleToggleMember = (id) => {
    setSelectedMembers(prev =>
      prev.includes(id) ? prev.filter(mid => mid !== id) : [...prev, id]
    );
  };

  // Toggle send to all
  const handleSendToAll = () => {
    setSendToAll(val => !val);
    setSelectedMembers([]);
    setSearch('');
  };

  // Insert placeholder at cursor position
  const insertPlaceholder = (placeholder) => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.focus();
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = message;
      const newText = text.substring(0, start) + `{${placeholder}}` + text.substring(end);
      setMessage(newText);
      
      setTimeout(() => {
        const newPosition = start + placeholder.length + 2;
        textarea.setSelectionRange(newPosition, newPosition);
        textarea.focus();
      }, 0);
    } else {
      setMessage((prev) => prev + `{${placeholder}}`);
    }
  };

  // Balance-related placeholders that require balance calculation
  const balancePlaceholders = ['balance', 'payment_status', 'overpaid_amount', 'contribution_balance'];
  
  // Validate placeholders
  const validatePlaceholders = (msg) => {
    if (!msg) {
      setInvalidPlaceholders([]);
      return true;
    }
    const placeholderPattern = /\{([^}]+)\}/g;
    const foundPlaceholders = [];
    let match;
    while ((match = placeholderPattern.exec(msg)) !== null) {
      foundPlaceholders.push(match[1]);
    }
    
    // Check for invalid placeholders (not in available list OR balance placeholders when balance can't be calculated)
    const invalid = foundPlaceholders.filter(ph => {
      // If it's a balance placeholder and balance can't be calculated, it's invalid
      if (balancePlaceholders.includes(ph) && !canCalculateBalance) {
        return true;
      }
      // If it's not in available placeholders, it's invalid
      if (!availablePlaceholders.includes(ph)) {
        return true;
      }
      return false;
    });
    
    setInvalidPlaceholders(invalid);
    return invalid.length === 0;
  };

  // Format message preview with highlighted placeholders
  const formatMessagePreview = (msg) => {
    if (!msg) return '';
    const parts = msg.split(/(\{[^}]+\})/g);
    return parts.map((part, index) => {
      if (part.match(/^\{[^}]+\}$/)) {
        const placeholderName = part.slice(1, -1);
        const isValid = availablePlaceholders.includes(placeholderName);
        return (
          <span
            key={index}
            className={isValid ? 'px-1 py-0.5 rounded bg-green-100 text-green-800 font-mono text-sm' : 'px-1 py-0.5 rounded bg-red-100 text-red-800 font-mono text-sm'}
          >
            {part}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  // Handle message change
  const handleMessageChange = (e) => {
    const newValue = e.target.value;
    setMessage(newValue);
    if (hasValidated) {
      validatePlaceholders(newValue);
    }
  };

  // Handle blur - validate when user leaves the field
  const handleMessageBlur = () => {
    setHasValidated(true);
    const isValid = validatePlaceholders(message);
    if (!isValid && invalidPlaceholders.length > 0) {
      toast.error(
        `Invalid placeholders: {${invalidPlaceholders.join('}, {')}}. ` +
        `Available: {${availablePlaceholders.join('}, {')}}`
      );
    }
  };

  // Send SMS
  const handleSendSms = async () => {
    if (!message.trim()) {
      window.alert('Please enter a message to send.');
      return;
    }
    
    // Validate placeholders
    if (!validatePlaceholders(message)) {
      window.alert(`Invalid placeholders found: {${invalidPlaceholders.join('}, {')}}. Please use valid placeholders.`);
      return;
    }
    
    const totalRecipients = sendToAll ? members.length : selectedMembers.length;
    if (!quota || quota.remaining < totalRecipients || totalRecipients === 0) {
      window.alert('Insufficient SMS quota or no recipients selected.');
      return;
    }
    if (!sendToAll && selectedMembers.length === 0) {
      window.alert('Please select at least one member.');
      return;
    }
    if (sendToAll && members.length === 0) {
      window.alert('No members available.');
      return;
    }
    
    const periodText = sendStatements 
      ? ` (Last ${statementPeriod} ${statementPeriod === '1' ? 'month' : 'months'})`
      : '';
    
    const confirmMessage = `Send ${sendStatements ? 'statements' : 'SMS'}${periodText} to ${totalRecipients} recipient(s)? This will use ${totalRecipients} SMS from your quota.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    setIsSending(true);
    try {
      const payload = {
        message: message.trim(),
        member_ids: sendToAll ? [] : selectedMembers,
        send_to_all: sendToAll,
        send_statements: sendStatements,
        statement_period_months: sendStatements ? parseInt(statementPeriod) : undefined,
      };
      
      await api.post('/bulk-sms/send', payload);
      toast.success('Bulk SMS job created! SMS will be sent to recipients.');
      setMessage('');
      setSelectedMembers([]);
      setSendToAll(false);
      setSendStatements(false);
      setStatementPeriod('3');
      setInvalidPlaceholders([]);
      setHasValidated(false);
      // Refresh history
      if (showHistory) {
        fetchHistory(historyPage);
      }
    } catch (err) {
      window.alert(err?.response?.data?.message || 'Failed to send SMS.');
    } finally {
      setIsSending(false);
    }
  };

  const totalRecipients = sendToAll ? members.length : selectedMembers.length;
  const canSendSms = quota && quota.remaining >= totalRecipients && totalRecipients > 0;

  // Fetch bulk SMS history
  const fetchHistory = async (page = 1) => {
    setIsLoadingHistory(true);
    try {
      const res = await api.get('/bulk-sms/history', {
        params: { page, per_page: 10 }
      });
      if (res.data.success) {
        setHistory(res.data.data.jobs || []);
        setHistoryPagination(res.data.data.pagination || null);
        setHistoryPage(page);
      }
    } catch (err) {
      toast.error('Failed to load SMS history');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Fetch job details with recipients
  const fetchJobDetails = async (jobId) => {
    if (jobDetails[jobId]) {
      return; // Already loaded
    }
    try {
      const res = await api.get(`/bulk-sms/job/${jobId}/status`);
      if (res.data.success) {
        setJobDetails(prev => ({
          ...prev,
          [jobId]: res.data.data
        }));
      }
    } catch (err) {
      toast.error('Failed to load job details');
    }
  };

  // Toggle job expansion
  const toggleJobExpansion = (jobId) => {
    if (expandedJob === jobId) {
      setExpandedJob(null);
    } else {
      setExpandedJob(jobId);
      fetchJobDetails(jobId);
    }
  };

  // Load history when showing history tab
  useEffect(() => {
    if (showHistory && history.length === 0) {
      fetchHistory(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHistory]);

  // Auto-refresh job details for pending/processing jobs
  useEffect(() => {
    if (!showHistory || history.length === 0) return;

    const pendingJobs = history.filter(job => 
      job.status === 'pending' || job.status === 'processing'
    );

    if (pendingJobs.length === 0) return;

    const interval = setInterval(() => {
      pendingJobs.forEach(job => {
        fetchJobDetails(job.id);
      });
      // Also refresh the history list
      fetchHistory(historyPage);
    }, 5000); // Refresh every 5 seconds

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHistory, history, historyPage]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Pending' },
      processing: { bg: 'bg-blue-100', text: 'text-blue-800', icon: Clock, label: 'Processing' },
      completed: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Completed' },
      failed: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Failed' },
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  // Get recipient status badge
  const getRecipientStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Pending' },
      sent: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Sent' },
      delivered: { bg: 'bg-green-100', text: 'text-green-800', label: 'Delivered' },
      failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed' },
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  // Show access denied for non-admin users
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">
            You don't have permission to access bulk SMS functionality.
          </p>
        </div>
      </div>
    );
  }

  return (
    <PermissionPage permission={PERMISSIONS.SEND_BULK_SMS}>
    <div className="max-w-5xl mx-auto py-8 px-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Bulk SMS</h1>
        <p className="text-gray-600 mt-1">Send SMS reminders to group members</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setShowHistory(false)}
              className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 ${
                !showHistory
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Send className="w-4 h-4 mr-2" />
              Send Bulk SMS
            </button>
            <PermissionGate permission={PERMISSIONS.VIEW_SMS_HISTORY}>
            <button
              onClick={() => {
                setShowHistory(true);
                if (history.length === 0) {
                  fetchHistory(1);
                }
              }}
              className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 ${
                showHistory
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <History className="w-4 h-4 mr-2" />
              History
              {historyPagination && historyPagination.total > 0 && (
                <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-600">
                  {historyPagination.total}
                </span>
              )}
            </button>
            </PermissionGate>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {!showHistory ? (
        <>
          {/* SMS Quota Status */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <svg className="w-6 h-6 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <h2 className="text-lg font-semibold text-gray-900">SMS Quota</h2>
          </div>
          {quota && (
            <span className="text-sm text-gray-500">
              {quota.remaining} / {quota.allocated} remaining
            </span>
          )}
        </div>

        {isQuotaLoading ? (
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
            <div className="h-2 bg-gray-200 rounded"></div>
          </div>
        ) : quota ? (
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Remaining SMS</span>
              <span className="text-lg font-bold text-gray-900">
                {quota.remaining} / {quota.allocated}
              </span>
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
              <div 
                className={`h-2 rounded-full ${
                  quota.is_exhausted ? 'bg-red-500' : 
                  quota.is_low ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${(quota.remaining / quota.allocated) * 100}%` }}
              />
            </div>

            {quota.is_exhausted && (
              <div className="p-3 bg-red-50 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-md">
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <span className="text-sm text-red-800">
                    SMS quota exhausted! Please recharge to continue sending SMS.
                  </span>
                </div>
              </div>
            )}

            {quota.is_low && !quota.is_exhausted && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <span className="text-sm text-yellow-800">
                    Low SMS quota! You have {quota.remaining} SMS remaining.
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-gray-500 text-center">Failed to load SMS quota</div>
        )}
      </div>

      {/* Statement Sending Toggle */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <FileText className="w-5 h-5 text-blue-600 mr-2" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Send Contribution Statements</h2>
              <p className="text-sm text-gray-600">Include statement download links in SMS</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={sendStatements}
              onChange={(e) => {
                setSendStatements(e.target.checked);
                if (!e.target.checked) {
                  setStatementPeriod('3'); // Reset to default
                }
              }}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        {/* Period Selection (shown when sending statements) */}
        {sendStatements && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Statement Period
            </label>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              {[
                { value: '3', label: '3 Months' },
                { value: '6', label: '6 Months' },
                { value: '12', label: '1 Year' },
                { value: '24', label: '2 Years' }
              ].map(period => (
                <button
                  key={period.value}
                  type="button"
                  onClick={() => setStatementPeriod(period.value)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    statementPeriod === period.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {period.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Select the time period for the contribution statements
            </p>
          </div>
        )}
      </div>

      {/* Message Composition */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Compose Message</h2>
        
        {/* Draft Examples */}
        <div className="mb-4 p-3 bg-gray-50 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg">
          <p className="text-xs font-medium text-gray-700 mb-2">Quick templates (click to use):</p>
          <div className="space-y-1">
            {draftExamples.map((example, index) => (
              <button
                key={index}
                type="button"
                onClick={() => {
                  setMessage(example);
                  setHasValidated(false);
                  setInvalidPlaceholders([]);
                }}
                className="block w-full text-left px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded border border-transparent hover:border-gray-300 transition-colors"
              >
                {example}
              </button>
            ))}
          </div>
        </div>
        
        {/* Placeholder Buttons */}
        <div className="flex flex-wrap items-center gap-2 mb-2 p-2 bg-gray-50 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg">
          <span className="text-xs font-medium text-gray-600">Quick insert:</span>
          {availablePlaceholders.map((ph) => (
            <button
              key={ph}
              type="button"
              onClick={() => insertPlaceholder(ph)}
              className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md border transition-all cursor-pointer shadow-sm ${
                ph === 'statement_link' || ph === 'contribution_balance'
                  ? 'bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100 hover:border-blue-400 active:bg-blue-200'
                  : 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100 hover:border-green-400 active:bg-green-200'
              }`}
              title={`Insert {${ph}}`}
            >
              <Plus className="w-3 h-3 mr-1" />
              <span className="font-mono">{'{'}{ph}{'}'}</span>
            </button>
          ))}
        </div>
        {sendStatements && !hasPaymentRouting && (
          <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-xs text-yellow-800">
              <strong>Note:</strong> Payment routing is not enabled. The <code className="bg-yellow-100 px-1 rounded">{'{contribution_balance}'}</code> placeholder is not available. Enable payment routing in Settings to use balance-related placeholders.
            </p>
          </div>
        )}
        
        {message.includes('{contribution_balance}') && hasPaymentRouting && (
          <div className="mb-2 p-3 bg-red-50 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-md">
            <div className="flex items-start">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-red-800 font-medium mb-1">
                  ⚠️ Balance Calculation Warning
                </p>
                <p className="text-xs text-red-700">
                  The <code className="bg-red-100 px-1 rounded">{'{contribution_balance}'}</code> placeholder will only be replaced 
                  for members who have valid contribution targets set in Payment Routing settings. 
                  Members without targets will receive SMS without the balance placeholder to prevent confusion. 
                  Make sure all members have targets configured before sending.
                </p>
              </div>
            </div>
          </div>
        )}
        
        <textarea
          ref={textareaRef}
          className={`w-full border rounded-md p-3 mb-2 focus:outline-none focus:ring-2 ${
            invalidPlaceholders.length > 0 && hasValidated
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:ring-blue-500'
          }`}
          rows={4}
          value={message}
          onChange={handleMessageChange}
          onBlur={handleMessageBlur}
          placeholder={sendStatements 
            ? (hasPaymentRouting && canCalculateBalance
                ? "Hi {member_name}, your contribution statement is ready. Remaining to pay: {contribution_balance}. Download: {statement_link}"
                : "Hi {member_name}, your contribution statement is ready. Download: {statement_link}")
            : "Enter your SMS message here..."}
          maxLength={230}
        />
        <div className="text-xs text-gray-500 text-right">
          {message.length}/230 characters
        </div>

        {/* Preview with formatted placeholders */}
        {message && (
          <div className="mt-2 p-3 bg-gray-50 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-md">
            <p className="text-xs text-gray-500 mb-1 font-medium">Preview:</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {formatMessagePreview(message)}
            </p>
          </div>
        )}

        {/* Error message */}
        {invalidPlaceholders.length > 0 && hasValidated && (
          <div className="mt-2 p-2 bg-red-50 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-md">
            <div className="flex items-start">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-red-700 font-medium mb-1">
                  Invalid placeholders found:
                </p>
                <ul className="text-xs text-red-600 list-disc list-inside">
                  {invalidPlaceholders.map((ph) => (
                    <li key={ph}>{'{'}{ph}{'}'} is not valid</li>
                  ))}
                </ul>
                <p className="text-xs text-red-600 mt-1">
                  Valid placeholders: {availablePlaceholders.map(ph => `{${ph}}`).join(', ')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Help text */}
        <p className="text-xs text-gray-500 mt-2">
          Available placeholders: {availablePlaceholders.map(ph => `{${ph}}`).join(', ')}
        </p>
      </div>

      {/* Recipient Selection */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Select Recipients</h2>
        
        {/* Send to All Option */}
        <div 
          className="flex items-center p-3 bg-gray-50 rounded-lg mb-3 cursor-pointer hover:bg-gray-100"
          onClick={handleSendToAll}
        >
          <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
            sendToAll ? 'bg-green-500 border-green-500' : 'border-gray-300'
          }`}>
            {sendToAll && (
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </div>
          <div className="flex-1">
            <div className="font-semibold text-gray-900">Send to All Members</div>
            <div className="text-sm text-gray-600">{members.length} active members</div>
          </div>
        </div>

        {/* Individual Selection */}
        {!sendToAll && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-base font-medium text-gray-700">Select Individual Members</span>
              <button 
                onClick={handleSelectAll}
                className="text-green-600 font-medium hover:text-green-700"
              >
                {selectedMembers.length === filteredMembers.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            {/* Search Input */}
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Type member name or phone..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            {/* Available Members */}
            <div className="mb-3">
              <div className="text-sm text-gray-600 mb-2">Available Members</div>
              {filteredMembers.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {filteredMembers.map(m => (
                    <button
                      key={m.id}
                      onClick={() => handleToggleMember(m.id)}
                      className={`px-3 py-1 rounded border text-sm ${
                        selectedMembers.includes(m.id) 
                          ? 'bg-green-500 border-green-500 text-white' 
                          : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {m.name} ({m.phone_number})
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500 text-center py-2">
                  {search ? 'No members found matching your search' : 'No members available'}
                </div>
              )}
            </div>

            {/* Selected Members */}
            {selectedMembers.length > 0 && (
              <div>
                <div className="text-sm text-gray-600 mb-2">Selected Members ({selectedMembers.length})</div>
                <div className="space-y-2">
                  {members
                    .filter(m => selectedMembers.includes(m.id))
                    .map(m => (
                      <div key={m.id} className="flex items-center gap-2 bg-green-50 p-3 rounded-lg border border-green-200">
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-medium text-sm">
                          {m.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{m.name}</div>
                          <div className="text-sm text-gray-600">{m.phone_number}</div>
                        </div>
                        <button 
                          onClick={() => handleToggleMember(m.id)}
                          className="p-1 text-red-500 hover:text-red-700"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {selectedMembers.length === 0 && (
              <div className="text-gray-500 text-center py-4">
                No members selected. Search and select members above.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Send Button */}
      <button
        onClick={handleSendSms}
        disabled={!canSendSms || isSending}
        className={`w-full py-3 px-6 rounded-md font-semibold text-white transition-colors ${
          !canSendSms || isSending 
            ? 'bg-gray-400 cursor-not-allowed' 
            : 'bg-green-600 hover:bg-green-700'
        }`}
      >
        {isSending ? 'Sending...' : `Send SMS (${totalRecipients})`}
      </button>

      {/* Info Card */}
      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <h3 className="text-sm font-medium text-gray-900 mb-2">Important Notes:</h3>
        <ul className="text-xs text-gray-600 space-y-1">
          <li>• Each recipient counts as 1 SMS</li>
          <li>• Messages are limited to 230 characters</li>
          <li>• Only active members will receive SMS</li>
        </ul>
      </div>
        </>
      ) : (
        /* SMS History Tab */
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <History className="w-5 h-5 text-gray-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">SMS History</h2>
              {historyPagination && (
                <span className="ml-3 text-sm text-gray-500">
                  ({historyPagination.total} total)
                </span>
              )}
            </div>
            {historyPagination && historyPagination.total > 0 && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => fetchHistory(historyPage - 1)}
                  disabled={historyPage === 1 || isLoadingHistory}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {historyPage} of {historyPagination.last_page}
                </span>
                <button
                  onClick={() => fetchHistory(historyPage + 1)}
                  disabled={historyPage >= historyPagination.last_page || isLoadingHistory}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {isLoadingHistory ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Loading history...</p>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8">
              <History className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No SMS history found</p>
              <p className="text-sm text-gray-400 mt-1">Your bulk SMS jobs will appear here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((job) => {
                const details = jobDetails[job.id];
                const isExpanded = expandedJob === job.id;
                
                return (
                  <div key={job.id} className="border border-gray-200 rounded-lg">
                    <div
                      className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => toggleJobExpansion(job.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            {getStatusBadge(job.status)}
                            {job.send_statements && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                <FileText className="w-3 h-3 mr-1" />
                                Statements
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-1 line-clamp-2">{job.message}</p>
                          <div className="flex items-center space-x-4 text-xs text-gray-500 mt-2">
                            <span>Recipients: {job.total_recipients}</span>
                            <span>Sent: {job.sent_count || 0}</span>
                            <span>Failed: {job.failed_count || 0}</span>
                            <span>Created: {formatDate(job.created_at)}</span>
                          </div>
                        </div>
                        <div className="ml-4">
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>

                    {isExpanded && details && (
                      <div className="border-t border-gray-200 p-4 bg-gray-50">
                        <div className="mb-4">
                          <h3 className="text-sm font-semibold text-gray-900 mb-2">Status Breakdown</h3>
                          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 kpi-grid">
                            <div className="text-center">
                              <div className="text-2xl font-bold text-gray-400">{details.status_breakdown.pending}</div>
                              <div className="text-xs text-gray-500">Pending</div>
                            </div>
                            <div className="text-center">
                              <div className="text-2xl font-bold text-blue-600">{details.status_breakdown.sent}</div>
                              <div className="text-xs text-gray-500">Sent</div>
                            </div>
                            <div className="text-center">
                              <div className="text-2xl font-bold text-green-600">{details.status_breakdown.delivered}</div>
                              <div className="text-xs text-gray-500">Delivered</div>
                            </div>
                            <div className="text-center">
                              <div className="text-2xl font-bold text-red-600">{details.status_breakdown.failed}</div>
                              <div className="text-xs text-gray-500">Failed</div>
                            </div>
                          </div>
                        </div>

                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 mb-2">Recipients ({details.recipients?.length || 0})</h3>
                          <div className="max-h-96 overflow-y-auto">
                            <table className="responsive-table min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sent At</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {details.recipients?.map((recipient) => (
                                  <tr key={recipient.id} className="hover:bg-gray-50">
                                    <td data-label="Member" className="px-3 py-2 text-sm text-gray-900">{recipient.member_name}</td>
                                    <td data-label="Phone" className="px-3 py-2 text-sm text-gray-600">{recipient.phone_number}</td>
                                    <td data-label="Status" className="px-3 py-2">{getRecipientStatusBadge(recipient.status)}</td>
                                    <td data-label="Sent At" className="px-3 py-2 text-sm text-gray-500">{formatDate(recipient.sent_at)}</td>
                                    <td data-label="Error" className="px-3 py-2 text-sm text-red-600">
                                      {recipient.error_message ? (
                                        <span className="truncate max-w-xs block" title={recipient.error_message}>
                                          {recipient.error_message}
                                        </span>
                                      ) : (
                                        '-'
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
    </PermissionPage>
  );
} 